import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchMenu, X } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import { IconButton } from 'shared/components/ui/icon-button';
import { useDelayedCallback } from 'shared/hooks/use-delayed-callback';
import { useKeysDown } from 'shared/hooks/use-keys-down';

import * as Styled from './search-bar.style';

export type SearchBarProps = {
  value?: string;
  onChange: (value: string) => void;
  onShowPage?: (value: string) => void;
  autoFocusInput?: boolean;
};

export const SearchBar: React.FC<SearchBarProps> = ({
  value,
  onChange,
  onShowPage,
  autoFocusInput,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const [input, setInput] = React.useState(value ?? '');

  const [_onChange] = useDelayedCallback(onChange, 350);

  const onChangeInput = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    setInput(target.value);
    _onChange(target.value);
  };

  const clear = () => {
    setInput('');
    _onChange('');
  };

  const onSubmit = (e: KeyboardEvent | React.MouseEvent) => {
    if (input) {
      e.preventDefault();
      e.stopPropagation();
      onShowPage?.(input);
    }
  };

  const onEnter = (e: KeyboardEvent | React.MouseEvent) => {
    if (document.activeElement && document.activeElement === inputRef.current) {
      onSubmit(e);
      !!input && inputRef.current?.blur();
    }
  };

  useKeysDown(['Enter'], onEnter, { otherKeysInCombinationAllowed: true });

  useEffect(() => {
    autoFocusInput && inputRef.current?.focus();
  }, [autoFocusInput]);

  return (
    <Styled.InputContainer>
      <Styled.IconContainer>
        <Icon icon={SearchMenu} />
      </Styled.IconContainer>

      <Styled.Input
        ref={inputRef}
        value={input}
        onChange={onChangeInput}
        placeholder={t('general.labels.search.placeholder')}
      />

      {!!input && <IconButton icon={X} onClick={clear} tabIndex={-1} />}
    </Styled.InputContainer>
  );
};
